"use client"

import { useState, type ReactNode } from "react"

const ON_CHAIN_ADDRESSES = [
  {
    label: "JETTON_MINTER",
    address: "EQDgEhvvWkTJJjhqdTN-nS-EaJi72ENzROHxFCx8PaqnpskS",
    short: "EQDg…skS",
    icon: "token",
    meta: "RIVYU Token Contract",
  },
  {
    label: "FOUNDER_VESTING",
    address: "EQB1dWzdpCOmuflhfSeiiQbL_zkhZjen-rPfnwJ1mJXQCxXm",
    short: "EQB1…Xxm",
    icon: "lock",
    meta: "12% Allocation · 12-MO Lock",
  },
  {
    label: "STAKING_POOL",
    address: "EQDON4HF7jcBHsIB9NgT_tcvkBLMWjQvEW2__YZkzkUaUQ78",
    short: "EQDO…Q78",
    icon: "staking",
    meta: "Up to 60% APY",
  },
  {
    label: "MINING_REWARDS",
    address: "EQCFiBp1fA0BKZMkn-b_ce87w1X1LdvrxQWO9XiEwTbLtmtp",
    short: "EQCFi…mtp",
    icon: "mining",
    meta: "30% Community Allocation",
  },
  {
    label: "REWARDS_POOL",
    address: "EQBe-wJBCrZUyDk3LzMXExtIP3Bt8k-SW9IWGwlC8hdNDmg-",
    short: "EQBe…mg-",
    icon: "pool",
    meta: "Ecosystem Rewards",
  },
  {
    label: "UTILITY_POOL",
    address: "EQButxTe3c-Kq5Kk9hxTHR5oUHjJqCrtskx-5U4eg_GOVGRz",
    short: "EQBu…Rz",
    icon: "utility",
    meta: "Protocol Utility Reserve",
  },
  {
    label: "CHEST_SHOP",
    address: "EQATuHyDGm0N3sIngp7IJemCGgYuZH5_aLR4e-R4Mjcz5BzM",
    short: "EQAT…BzM",
    icon: "chest",
    meta: "Loot Chest Marketplace",
  },
] as const

function AddressIcon({ type }: { type: string }) {
  const props = {
    width: 24,
    height: 24,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  }
  switch (type) {
    case "lock":
      return (
        <svg {...props}>
          <rect x="3" y="11" width="18" height="11" rx="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      )
    case "staking":
      return (
        <svg {...props}>
          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
          <polyline points="17 6 23 6 23 12" />
        </svg>
      )
    case "mining":
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="3" />
          <path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83" />
        </svg>
      )
    case "pool":
      return (
        <svg {...props}>
          <path d="M2 12a5 5 0 0 0 5 5 8 8 0 0 1 5 2 8 8 0 0 1 5-2 5 5 0 0 0 5-5V7H2Z" />
          <path d="M6 11V7M10 11V7M14 11V7M18 11V7" />
        </svg>
      )
    case "utility":
      return (
        <svg {...props}>
          <polygon points="12 2 2 7 12 12 22 7 12 2" />
          <polyline points="2 17 12 22 22 17" />
          <polyline points="2 12 12 17 22 12" />
        </svg>
      )
    case "chest":
      return (
        <svg {...props}>
          <path d="M5 3H19A2 2 0 0 1 21 5V19A2 2 0 0 1 19 21H5A2 2 0 0 1 3 19V5A2 2 0 0 1 5 3Z" />
          <path d="M3 9h18M9 21V9" />
        </svg>
      )
    default:
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8v4l3 3" />
        </svg>
      )
  }
}

type RingDef = {
  id: string
  r: number
  label: string
  meta: string
  spin: number
  dir: 1 | -1
  accent: string
}

const RINGS: RingDef[] = [
  { id: "comm", r: 240, label: "COMMUNITY_FIRST", meta: "30% mined · 0% team @ TGE", spin: 90, dir: 1, accent: "#8fbfa8" },
  { id: "tran", r: 180, label: "TRANSPARENCY", meta: "on-chain · weekly reports", spin: 70, dir: -1, accent: "#00e5ff" },
  { id: "sec", r: 130, label: "SECURITY", meta: "TSA remediated · human audit planned", spin: 50, dir: 1, accent: "#8fbfa8" },
  { id: "acc", r: 80, label: "ACCOUNTABILITY", meta: "19 Sep 2026 · subject to audit", spin: 32, dir: -1, accent: "#c9a96a" },
]

function AboutRingsStage() {
  const [hover, setHover] = useState<string | null>(null)

  return (
    <div className="about-rings-root">
      <div className="about-rings-hud about-rings-hud--top">
        <span style={{ color: "var(--green-bright)" }}>// CORE.LOGIC — RING_STACK</span>
        <span style={{ color: "var(--text-faint)" }}>4 PRINCIPLES</span>
      </div>
      <svg viewBox="0 0 600 720" preserveAspectRatio="xMidYMid meet" className="about-rings-svg" aria-hidden>
        <defs>
          <radialGradient id="centreGlowAb" cx="50%" cy="50%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.9)" />
            <stop offset="30%" stopColor="rgba(143,191,168,0.6)" />
            <stop offset="100%" stopColor="rgba(0,229,255,0)" />
          </radialGradient>
        </defs>
        <circle cx="300" cy="360" r="280" fill="none" stroke="#232C29" strokeDasharray="1 6" />
        <circle cx="300" cy="360" r="300" fill="none" stroke="#1E2D27" />
        {Array.from({ length: 36 }).map((_, i) => {
          const a = (i / 36) * Math.PI * 2
          const r1 = 296
          const r2 = i % 9 === 0 ? 280 : 290
          return (
            <line
              key={i}
              x1={300 + Math.cos(a) * r1}
              y1={360 + Math.sin(a) * r1}
              x2={300 + Math.cos(a) * r2}
              y2={360 + Math.sin(a) * r2}
              stroke="#3B4A43"
              strokeWidth={i % 9 === 0 ? 1.4 : 0.8}
            />
          )
        })}
        {RINGS.map((ring) => {
          const isHover = hover === ring.id
          const tickCount = Math.round(ring.r / 6)
          const dashLen = (ring.r * Math.PI * 2) / tickCount
          const spinName = ring.dir > 0 ? "ring-spin-r" : "ring-spin-l"
          return (
            <g
              key={ring.id}
              onMouseEnter={() => setHover(ring.id)}
              onMouseLeave={() => setHover(null)}
              style={{ cursor: "crosshair" }}
            >
              <circle cx="300" cy="360" r={ring.r} fill="none" stroke="transparent" strokeWidth="22" />
              <circle
                cx="300"
                cy="360"
                r={ring.r}
                fill="none"
                stroke={ring.accent}
                strokeOpacity={isHover ? 0.95 : 0.45}
                strokeWidth={isHover ? 1.8 : 1}
              />
              <g
                style={{
                  transformOrigin: "300px 360px",
                  animation: `${spinName} ${ring.spin}s linear infinite`,
                }}
              >
                <circle
                  cx="300"
                  cy="360"
                  r={ring.r}
                  fill="none"
                  stroke={ring.accent}
                  strokeOpacity={isHover ? 0.7 : 0.32}
                  strokeWidth="2"
                  strokeDasharray={`2 ${dashLen - 2}`}
                />
                <circle cx={300 + ring.r} cy="360" r={isHover ? 5 : 3.5} fill={ring.accent}>
                  <animate attributeName="opacity" values="1;0.3;1" dur="1.4s" repeatCount="indefinite" />
                </circle>
                <circle cx={300 - ring.r} cy="360" r="2.5" fill={ring.accent} opacity="0.6" />
              </g>
              <path
                id={`arc-ab-${ring.id}`}
                d={`M ${300 - ring.r * 0.92} ${360 - ring.r * 0.4} A ${ring.r} ${ring.r} 0 0 1 ${300 + ring.r * 0.92} ${360 - ring.r * 0.4}`}
                fill="none"
              />
              <text fontSize="10" fontFamily="var(--mono)" fontWeight="600" letterSpacing="4" fill={isHover ? "#ffffff" : ring.accent}>
                <textPath href={`#arc-ab-${ring.id}`} startOffset="50%" textAnchor="middle">
                  {ring.label}
                </textPath>
              </text>
              {isHover && (
                <text x="300" y={360 + ring.r + 16} textAnchor="middle" fill="#b6c4bb" fontSize="9" fontFamily="var(--mono)" letterSpacing="3">
                  {ring.meta}
                </text>
              )}
            </g>
          )
        })}
        <line x1="0" y1="360" x2="60" y2="360" stroke="#3B4A43" />
        <line x1="540" y1="360" x2="600" y2="360" stroke="#3B4A43" />
        <line x1="300" y1="20" x2="300" y2="60" stroke="#3B4A43" />
        <line x1="300" y1="660" x2="300" y2="700" stroke="#3B4A43" />
        <circle cx="300" cy="360" r="60" fill="url(#centreGlowAb)" opacity="0.9" />
        <circle cx="300" cy="360" r="14" fill="rgba(255,255,255,0.95)">
          <animate attributeName="r" values="12;18;12" dur="2.4s" repeatCount="indefinite" />
        </circle>
        <circle cx="300" cy="360" r="22" fill="none" stroke="#8fbfa8" strokeWidth="1">
          <animate attributeName="r" values="22;48;22" dur="3s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.8;0;0.8" dur="3s" repeatCount="indefinite" />
        </circle>
        <text x="300" y="438" textAnchor="middle" fill="#475149" fontSize="8" fontFamily="var(--mono)" letterSpacing="3">
          // CORE.LOGIC
        </text>
        <text x="300" y="452" textAnchor="middle" fill="#8fbfa8" fontSize="9" fontFamily="var(--mono)" letterSpacing="3">
          RIVYU.SYS
        </text>
      </svg>
      <div className="about-rings-hud about-rings-hud--bottom">
        <span style={{ color: "var(--green-soft)" }}>◆ HOVER_TO_INSPECT</span>
        <span style={{ color: "var(--text-faint)" }}>
          {hover ? `INSPECT: ${RINGS.find((r) => r.id === hover)?.label ?? ""}` : "IDLE"}
        </span>
      </div>
    </div>
  )
}

type ValueCard = {
  id: string
  num: string
  label: string
  meta: string
  accent: string
  body: ReactNode
}

const ABOUT_VALUES: ValueCard[] = [
  {
    id: "acc",
    num: "01",
    label: "ACCOUNTABILITY",
    meta: "19 Sep 2026 target · subject to audit + legal",
    accent: "#c9a96a",
    body: (
      <>
        We commit publicly to one TGE target date and ship the gating work in the open. TGE is <strong>targeted for 19 September 2026</strong>, subject to completion of the independent audit and legal-entity setup.
      </>
    ),
  },
  {
    id: "sec",
    num: "02",
    label: "SECURITY",
    meta: "TSA remediated · human audit planned",
    accent: "#8fbfa8",
    body: (
      <>
        FunC contracts passed an <strong>AI-assisted TSA review</strong> (Apr 2026); actionable findings were{" "}
        <strong>remediated May 2026</strong> with regression tests. An <strong>independent firm audit</strong> is planned
        before mainnet migration. Bug bounty program in preparation. Every state transition observable on-chain.
      </>
    ),
  },
  {
    id: "tran",
    num: "03",
    label: "TRANSPARENCY",
    meta: "on-chain · weekly reports",
    accent: "#00e5ff",
    body: (
      <>
        All seven contract addresses are public. Weekly reports. Audit trails. <strong>What you see is what you trust.</strong>
      </>
    ),
  },
  {
    id: "comm",
    num: "04",
    label: "COMMUNITY_FIRST",
    meta: "30% mined · 0% team @ TGE",
    accent: "#8fbfa8",
    body: (
      <>
        30% of supply distributed via fair mining. Founders locked for 12 months. <strong>You&apos;re co-owners — not exit liquidity.</strong>
      </>
    ),
  },
]

export function AboutView() {
  const [hover, setHover] = useState<string | null>(null)

  return (
    <div className="about-wrap">
      <div className="about-left">
        <h2>
          The Core <span className="accent">Logic_</span>
        </h2>
        <p className="about-intro">
          // We&apos;re on a mission to restore trust in token launches. Every decision answers one question —{" "}
          <strong style={{ color: "var(--green-bright)" }}>does this protect the community?</strong>
        </p>
        <div className="about-values-label">// VALUES.SYS — 4 PRINCIPLES</div>
        {ABOUT_VALUES.map((v) => {
          const klass = v.accent === "#00e5ff" ? "cyan" : v.accent === "#c9a96a" ? "amber" : ""
          return (
            <div
              key={v.id}
              className={`v2-value-card ${klass} ${hover === v.id ? "active" : ""}`}
              onMouseEnter={() => setHover(v.id)}
              onMouseLeave={() => setHover(null)}
            >
              <div className="head">
                <span style={{ color: v.accent }}>{v.label}</span>
                <span className="id">// {v.num}</span>
              </div>
              <div className="body">{v.body}</div>
              <div className="meta">{v.meta}</div>
            </div>
          )
        })}
        <div className="about-chain-banner">◆ ON_CHAIN_PUBLISHED · 7 CONTRACTS · TON_TESTNET</div>

        <div className="about-onchain">
          <div className="about-onchain-head">
            <span>// ON_CHAIN.PUBLISHED</span>
            <span className="about-onchain-badge">⚠ TESTNET</span>
          </div>
          {ON_CHAIN_ADDRESSES.map((item) => (
            <div key={item.label} className="about-onchain-row">
              <div className="about-onchain-addr">
                <div className="about-onchain-label">{item.label}</div>
                <a
                  href={`https://testnet.tonscan.org/address/${item.address}`}
                  target="_blank"
                  rel="noreferrer"
                  title={item.address}
                  className="about-onchain-link"
                >
                  {item.short}
                </a>
              </div>
              <div className="about-onchain-icon">
                <AddressIcon type={item.icon} />
              </div>
              <div className="about-onchain-meta">{item.meta}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="about-right">
        <AboutRingsStage />
      </div>
    </div>
  )
}
